import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authService, extractApiError } from '../api/services';
import './Auth.css';
import logo from '../assets/rush-republic-logo.png';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Mirrors Signup.js's own list -- same rules the backend's enforceStrongPassword checks. */
const PASSWORD_RULES = [
  { test: (v) => v.length >= 8, label: 'At least 8 characters' },
  { test: (v) => /[A-Z]/.test(v), label: 'One uppercase letter' },
  { test: (v) => /[a-z]/.test(v), label: 'One lowercase letter' },
  { test: (v) => /\d/.test(v), label: 'One number' },
  { test: (v) => /[!@#$%^&*(),.?":{}|<>_\-[\];'\\/`~+=]/.test(v), label: 'One special character' },
];

const RESEND_COOLDOWN_SECONDS = 30;

/**
 * One linear flow, one page: request -> otp -> reset -> done. Nothing here
 * needs to be deep-linkable by step -- the backend is authoritative at
 * every transition regardless of what the UI currently shows.
 */
export default function ForgotPassword() {
  const [step, setStep] = useState('request');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const startResendCooldown = () => {
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    const tick = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(tick);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const requestOtp = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');

    const trimmed = email.trim();
    if (!trimmed) {
      setError('Enter the Email');
      return;
    }
    if (!EMAIL_RE.test(trimmed)) {
      setError('Enter a valid Email');
      return;
    }

    setSubmitting(true);
    try {
      const data = await authService.forgotPassword(trimmed);
      setInfo(data.message);
      setStep('otp');
      startResendCooldown();
    } catch (err) {
      setError(extractApiError(err, 'Something went wrong. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const resendOtp = async () => {
    if (resendCooldown > 0 || submitting) return;
    setError('');
    setInfo('');
    setSubmitting(true);
    try {
      const data = await authService.forgotPassword(email.trim());
      setInfo(data.message);
      setOtp('');
      startResendCooldown();
    } catch (err) {
      setError(extractApiError(err, 'Something went wrong. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');

    if (!/^\d{6}$/.test(otp)) {
      setError('Enter the 6-digit OTP.');
      return;
    }

    setSubmitting(true);
    try {
      const data = await authService.verifyResetOtp(email.trim(), otp);
      setResetToken(data.reset_token);
      setStep('reset');
    } catch (err) {
      setError(extractApiError(err, 'Invalid or expired OTP.'));
    } finally {
      setSubmitting(false);
    }
  };

  const failedRules = PASSWORD_RULES.filter((rule) => !rule.test(password));

  const submitNewPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (!password || !confirmPassword) {
      setError('Both password fields are required.');
      return;
    }
    if (failedRules.length > 0) {
      setError(`Password must include: ${failedRules.map((r) => r.label.toLowerCase()).join(', ')}.`);
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }

    setSubmitting(true);
    try {
      await authService.resetPassword(email.trim(), resetToken, password, confirmPassword);
      setStep('done');
    } catch (err) {
      setError(extractApiError(err, 'Could not reset your password. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rr-auth">
      <div className="rr-auth__panel">
        <div className="rr-auth__brand">
          <img src={logo} alt="Rush Republic" className="rr-auth__logo" />
          <h1 className="rr-auth__company">Rush Republic</h1>
          <p className="rr-auth__tagline">Employee Management Portal</p>
        </div>
        <hr className="rr-auth__divider" />
        <h2 className="rr-auth__title">Forgot Password</h2>

        {error && <div className="rr-form-error">{error}</div>}
        {info && step !== 'request' && <div className="rr-form-success">{info}</div>}

        {step === 'request' && (
          <form onSubmit={requestOtp} noValidate>
            <div className="rr-field">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <button type="submit" className="rr-submit" disabled={submitting}>
              {submitting ? 'Sending…' : 'Send OTP'}
            </button>
          </form>
        )}

        {step === 'otp' && (
          <form onSubmit={verifyOtp} noValidate>
            <p style={{ fontSize: 13, color: 'var(--rr-grey)', marginBottom: 12 }}>
              Enter the 6-digit OTP sent to your email.
            </p>
            <div className="rr-field">
              <label htmlFor="otp">OTP</label>
              <input
                id="otp"
                name="otp"
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                autoComplete="one-time-code"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              />
            </div>
            <button type="submit" className="rr-submit" disabled={submitting}>
              {submitting ? 'Verifying…' : 'Verify OTP'}
            </button>
            <p className="rr-auth__switch">
              {resendCooldown > 0 ? (
                <span>Resend OTP in {resendCooldown}s</span>
              ) : (
                <button
                  type="button"
                  onClick={resendOtp}
                  disabled={submitting}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                >
                  <span style={{ color: 'var(--rr-black)', fontWeight: 700, borderBottom: '1px solid var(--rr-black)' }}>
                    Resend OTP
                  </span>
                </button>
              )}
            </p>
          </form>
        )}

        {step === 'reset' && (
          <form onSubmit={submitNewPassword} noValidate>
            <div className="rr-field">
              <label htmlFor="password">Enter New Password</label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="rr-field">
              <label htmlFor="confirmPassword">Re-Enter New Password</label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
            <button type="submit" className="rr-submit" disabled={submitting}>
              {submitting ? 'Resetting…' : 'Reset Password'}
            </button>
          </form>
        )}

        {step === 'done' && (
          <>
            <div className="rr-form-success">
              Password reset successfully. Please log in with your new password.
            </div>
            <p className="rr-auth__switch">
              <Link to="/login">Back to Login</Link>
            </p>
          </>
        )}

        {step !== 'done' && (
          <p className="rr-auth__switch">
            <Link to="/login">Back to Login</Link>
          </p>
        )}
      </div>
    </div>
  );
}
